import React, { useState, useEffect } from 'react';
import { Clock } from 'lucide-react';
import api from '../utils/api';

// UAE Timezone: UTC+4
const UAE_TIMEZONE = 'Asia/Dubai';

// ===== Hijri / Gregorian helpers =====
// Format Gregorian date in UAE timezone as plain DD/MM/YYYY (no RTL marks)
export const formatGregorianUAE = (date) => {
  try {
    // en-CA returns YYYY-MM-DD; rearrange to DD/MM/YYYY manually so no RTL marks creep in
    const iso = new Intl.DateTimeFormat('en-CA', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      timeZone: UAE_TIMEZONE
    }).format(date);
    const [y, m, d] = iso.split('-');
    if (!y || !m || !d) return '';
    return `${d}/${m}/${y}`;
  } catch (e) {
    return '';
  }
};

// Format Hijri (Islamic Umm al-Qura) date in UAE timezone with optional day offset.
export const formatHijriUAE = (date, offsetDays = 0) => {
  try {
    const d = new Date(date.getTime() + (offsetDays || 0) * 86400000);
    return new Intl.DateTimeFormat('ar-SA-u-ca-islamic-umalqura', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
      timeZone: UAE_TIMEZONE
    }).format(d);
  } catch (e) {
    return '';
  }
};

export const getUAETime = () => {
  try {
    const dateStr = new Date().toLocaleString('en-US', { timeZone: UAE_TIMEZONE });
    const date = new Date(dateStr);
    // Validate the date is valid
    if (isNaN(date.getTime())) {
      console.warn('Invalid UAE time, falling back to local time');
      return new Date();
    }
    return date;
  } catch (error) {
    console.warn('Error getting UAE time:', error);
    return new Date();
  }
};

export const getUAEDateString = () => {
  const uaeDate = getUAETime();
  const year = uaeDate.getFullYear();
  const month = String(uaeDate.getMonth() + 1).padStart(2, '0');
  const day = String(uaeDate.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// DD/MM/YYYY format
export const getUAEDateFormatted = () => {
  const uaeDate = getUAETime();
  const year = uaeDate.getFullYear();
  const month = String(uaeDate.getMonth() + 1).padStart(2, '0');
  const day = String(uaeDate.getDate()).padStart(2, '0');
  return `${day}/${month}/${year}`;
};

// Arabic date format: الأربعاء 4 مارس 2026
export const getUAEDateArabic = () => {
  const uaeDate = getUAETime();
  return uaeDate.toLocaleDateString('ar-AE', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    timeZone: UAE_TIMEZONE
  });
};

// Format any date to DD/MM/YYYY
export const formatDateDDMMYYYY = (date) => {
  if (!date) return '';
  try {
    const d = new Date(date);
    if (isNaN(d.getTime())) return '';
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    return `${day}/${month}/${year}`;
  } catch (error) {
    console.warn('Error formatting date:', error);
    return '';
  }
};

// Convert any ISO datetime to UAE local date `YYYY-MM-DD`.
// Use for revenue/expense rows so that 00:30 AM UAE entries don't display under
// the previous UTC date.
export const toUAEDateString = (isoDate) => {
  if (!isoDate) return '';
  try {
    const d = new Date(isoDate);
    if (isNaN(d.getTime())) return '';
    // en-CA returns YYYY-MM-DD format
    return d.toLocaleDateString('en-CA', { timeZone: UAE_TIMEZONE });
  } catch (error) {
    return '';
  }
};

// Format any ISO datetime to UAE-local DD/MM/YYYY.
export const formatUAEDateDDMMYYYY = (isoDate) => {
  const ymd = toUAEDateString(isoDate);
  if (!ymd) return '';
  const [y, m, d] = ymd.split('-');
  return `${d}/${m}/${y}`;
};

// Day-of-week (Arabic) of an ISO datetime evaluated at UAE local time.
export const getUAEDayName = (isoDate) => {
  if (!isoDate) return '';
  try {
    const d = new Date(isoDate);
    if (isNaN(d.getTime())) return '';
    return d.toLocaleDateString('ar-AE', { weekday: 'long', timeZone: UAE_TIMEZONE });
  } catch (error) {
    return '';
  }
};

// HH:MM time of an ISO datetime evaluated at UAE local time.
export const getUAETimeHHMM = (isoDate) => {
  if (!isoDate) return '';
  try {
    const d = new Date(isoDate);
    if (isNaN(d.getTime())) return '';
    return d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', timeZone: UAE_TIMEZONE });
  } catch (error) {
    return '';
  }
};

// Format any date to Arabic: الأربعاء 4 مارس 2026
export const formatDateArabic = (date) => {
  if (!date) return '';
  try {
    const d = new Date(date);
    if (isNaN(d.getTime())) return '';
    return d.toLocaleDateString('ar-AE', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      timeZone: UAE_TIMEZONE
    });
  } catch (error) {
    console.warn('Error formatting date to Arabic:', error);
    return '';
  }
};

export const getUAETimeString = () => {
  const uaeDate = getUAETime();
  const hours = String(uaeDate.getHours()).padStart(2, '0');
  const minutes = String(uaeDate.getMinutes()).padStart(2, '0');
  return `${hours}:${minutes}`;
};

export const formatUAEDateTime = (date) => {
  if (!date) return '';
  try {
    const d = new Date(date);
    if (isNaN(d.getTime())) return '';
    return d.toLocaleString('ar-AE', { 
      timeZone: UAE_TIMEZONE,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  } catch (error) {
    console.warn('Error formatting UAE datetime:', error);
    return '';
  }
};

// Check timezone difference between device and UAE
// Returns { isOutsideUAE: boolean, differenceMinutes: number }
export const checkTimezoneOffset = () => {
  const now = new Date();
  const uaeTime = getUAETime();
  
  // Get difference in minutes
  const deviceTime = now.getTime();
  const uaeTimeMs = uaeTime.getTime();
  const differenceMinutes = Math.abs(deviceTime - uaeTimeMs) / (1000 * 60);
  
  return {
    isOutsideUAE: differenceMinutes > 45,
    differenceMinutes: Math.round(differenceMinutes)
  };
};

const UAEClock = ({ showDate = true, compact = false, className = '' }) => {
  const [currentTime, setCurrentTime] = useState(getUAETime());
  const [dateDisplay, setDateDisplay] = useState({
    show_gregorian: true,
    show_hijri: true,
    hijri_offset_days: 0,
  });

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(getUAETime());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    let active = true;
    const load = async () => {
      try {
        const res = await api.get('/settings/datetime-display');
        if (active && res?.data) {
          setDateDisplay({
            show_gregorian: res.data.show_gregorian !== false,
            show_hijri: res.data.show_hijri !== false,
            hijri_offset_days: Number(res.data.hijri_offset_days) || 0,
          });
        }
      } catch (_) {
        // public endpoint — silently fall back to defaults
      }
    };
    load();
    const reload = setInterval(load, 5 * 60 * 1000); // refresh every 5min so admin changes propagate
    return () => { active = false; clearInterval(reload); };
  }, []);

  const formatTime = (date) => {
    return date.toLocaleTimeString('ar-AE', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true
    });
  };

  const gregorianStr = dateDisplay.show_gregorian ? formatGregorianUAE(currentTime) : '';
  const hijriStr = dateDisplay.show_hijri
    ? formatHijriUAE(currentTime, dateDisplay.hijri_offset_days)
    : '';

  // Intl 'ar-SA-u-ca-islamic-umalqura' already appends 'هـ' to the year — don't double-append.
  const hijriDisplay = hijriStr && !/هـ\s*$/.test(hijriStr) ? `${hijriStr} هـ` : hijriStr;

  if (compact) {
    return (
      <div className={`flex items-center gap-2 text-sm flex-wrap ${className}`} data-testid="uae-clock-compact">
        <Clock className="w-4 h-4 text-primary" />
        <span className="font-medium" dir="ltr">{formatTime(currentTime)}</span>
        {gregorianStr && (
          <span className="font-medium text-muted-foreground" dir="ltr" data-testid="uae-clock-gregorian">{gregorianStr}</span>
        )}
        {hijriDisplay && (
          <span className="font-medium text-amber-400" data-testid="uae-clock-hijri">{hijriDisplay}</span>
        )}
        <span className="text-muted-foreground text-xs">UAE</span>
      </div>
    );
  }

  return (
    <div className={`flex items-center gap-3 bg-secondary/30 px-4 py-2 rounded-lg ${className}`} data-testid="uae-clock">
      <Clock className="w-5 h-5 text-primary" />
      <div className="flex flex-col">
        <span className="text-lg font-bold" dir="ltr">{formatTime(currentTime)}</span>
        {showDate && (gregorianStr || hijriDisplay) && (
          <div className="flex items-center gap-2 flex-wrap">
            {gregorianStr && (
              <span className="text-lg font-bold text-muted-foreground" dir="ltr" data-testid="uae-clock-gregorian">
                {gregorianStr}
              </span>
            )}
            {gregorianStr && hijriDisplay && (
              <span className="text-lg font-bold text-muted-foreground/40">•</span>
            )}
            {hijriDisplay && (
              <span className="text-lg font-bold text-amber-400" data-testid="uae-clock-hijri">
                {hijriDisplay}
              </span>
            )}
          </div>
        )}
      </div>
      <span className="text-xs text-primary bg-primary/10 px-2 py-1 rounded">UAE</span>
    </div>
  );
};

export default UAEClock;
